import { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { useStore } from "../state/state.ts";
import windrose_url from "../assets/windrose.glb";
import floor_plan_mask_url from "../assets/floor_plan_mask.png";
import "./building-model.css";
import InfoButton from "./inputs/info-button.tsx";
import ModalBuildingGeometryData from "../modals/modal_building_geometry_data.tsx";

export default function BuildingModel(props?: { url?: string }) {
  const glb_url_store = useStore((state) => state.glb_url);
  const context_glb_url = useStore((state) => state.context_glb_url);
  const parcel_buildings = useStore((state) => state.parcel_buildings);
  const building_url = props?.url ?? glb_url_store;
  const loading_building_data = useStore((state) => state.loading_building_data);
  const siteplan = useStore((state) => state.siteplan);

  const container_ref = useRef<HTMLDivElement>(null);
  const camera_ref = useRef<THREE.PerspectiveCamera | null>(null);
  const controls_ref = useRef<OrbitControls | null>(null);
  const default_view_ref = useRef<{
    position: THREE.Vector3;
    target: THREE.Vector3;
  } | null>(null);
  const [status, setStatus] = useState<"idle" | "loading" | "loaded" | "error">(
    building_url ? "loading" : "idle",
  );

  const reset_view = () => {
    // const container = container_ref.current;
    // const width = container.clientWidth || 1;
    // const height = container.clientHeight || 1;

    const camera = camera_ref.current;
    const controls = controls_ref.current;
    const view = default_view_ref.current;
    if (!camera || !controls || !view) return;
    camera.position.copy(view.position);
    controls.target.copy(view.target);
    controls.update();
  };

  const top_view = () => {
    const camera = camera_ref.current;
    const controls = controls_ref.current;
    const view = default_view_ref.current;
    if (!camera || !controls || !view) return;
    const distance = view.position.distanceTo(view.target) * 2.5;
    controls.target.copy(view.target);
    camera.position.set(
      view.target.x,
      view.target.y + distance,
      view.target.z + distance * 0.001,
    );
    
    camera.fov = 10;
    camera.updateProjectionMatrix();

    controls.update();
  };

  useEffect(() => {
    const container = container_ref.current;
    if (!container) return;

    setStatus(building_url ? "loading" : "idle");

    const width = container.clientWidth || 1;
    const height = container.clientHeight || 1;

    const scene = new THREE.Scene();

    const camera = new THREE.PerspectiveCamera(10, width / height, 0.1, 10000);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setSize(width, height);
    renderer.autoClear = false;
    container.appendChild(renderer.domElement);

    const overlay_scene = new THREE.Scene();
    const overlay_camera = new THREE.OrthographicCamera(-1.3, 1.3, 1.3, -1.3, 0.1, 100);
    const overlay_size = 120;
    const overlay_margin_top = -18;
    const overlay_margin_right = -18;
    
    const key_light = new THREE.DirectionalLight(0xffffff, 2.0);
    key_light.position.set(1, 2, 1.5);
    scene.add(key_light);
    const fill_light = new THREE.DirectionalLight(0xffffff, 0.6);
    fill_light.position.set(-1, 0.5, -1);
    scene.add(fill_light);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.enablePan = false;
    controls.maxPolarAngle = Math.PI / 2 - 0.1;

    camera_ref.current = camera;
    controls_ref.current = controls;

    let disposed = false;
    let frame_id = 0;
    let model: THREE.Object3D | undefined;
    let windrose: THREE.Object3D | undefined;
    let floor_plan: THREE.Mesh | undefined;
    const windrose_holder = new THREE.Group();
    overlay_scene.add(windrose_holder);

    const frame_object = (object: THREE.Object3D) => {
      const box = new THREE.Box3().setFromObject(object);
      const size = box.getSize(new THREE.Vector3());

      const radius = Math.max(size.x, size.y, size.z) * 0.5 || 1;
      const fov = (camera.fov * Math.PI) / 180;
      const distance = (radius / Math.sin(fov / 2)) * 3;

      camera.position.set(distance, distance * 0.7, distance);
      camera.near = distance / 100;
      camera.far = distance * 100;
      camera.updateProjectionMatrix();


      controls.target.set(0, 5, 0);
      controls.maxDistance = distance * 2;
      controls.minDistance = distance * 0.75;
      controls.update();

      default_view_ref.current = {
        position: camera.position.clone(),
        target: controls.target.clone(),
      };
    };

    const roof_name_pattern = /^roof_\d+$/;
    const roof_material = new THREE.MeshStandardMaterial({ color: 0xff5533 });

    const wall_name_pattern = /^wall_\d+$/;
    const wall_material = new THREE.MeshStandardMaterial({ color: 0xffffff });

    const stripe_texture = create_stripe_texture();

    // Alpha mask for the site plan; `alphaMap` samples the green channel, so
    // the texture stays in linear space (no sRGB conversion).
    const floor_plan_alpha_texture = new THREE.TextureLoader().load(
      floor_plan_mask_url,
      () => {
        if (disposed || !floor_plan) return;
        (floor_plan.material as THREE.Material).needsUpdate = true;
      },
      undefined,
      (error) => {
        if (disposed) return;
        console.error("BuildingModel: failed to load floor plan mask", error);
      },
    );
    
    // Fallback ground texture, shown whenever no site plan tile is available.
    // The plane is 150 m wide, so three tiles give 50 m squares with 10 m cells.
    const grid_texture = create_grid_texture(3);

    // The ground is always in the scene; it starts on the grid and only swaps
    // to the site plan tile once that has actually loaded.
    const create_floor_plan = () => {
      const geometry = new THREE.PlaneGeometry(150, 150);
      const material = new THREE.MeshBasicMaterial({
        map: grid_texture,
        alphaMap: floor_plan_alpha_texture,
        transparent: true,
        depthWrite: false,
      });
      floor_plan = new THREE.Mesh(geometry, material);
      floor_plan.rotation.x = - Math.PI / 2
      floor_plan.position.y = -0.05
      scene.add(floor_plan);
    };

    create_floor_plan();

    const windrose_material = new THREE.MeshBasicMaterial({ 
      color: 0x000000,
    });

    const windrose_north_material = new THREE.MeshBasicMaterial({ 
      color: 0xff0000,
    });

     const windrose_sphere_material = new THREE.MeshBasicMaterial({ 
      color: 0xffffff,
      opacity: 0.75,
      transparent: true,
    });

    const below_grade_material = new THREE.MeshStandardMaterial({
      color: 0x808080
      //map: stripe_texture,
    });
    
    const parcel_building_material = new THREE.MeshStandardMaterial({ 
      color: 0x5555bb,
    });

    const context_building_material = new THREE.MeshStandardMaterial({ 
      color: 0xaaaaaa,
    });

    const loader = new GLTFLoader();

    
    if (building_url) {
      loader.load(
        windrose_url,
        (gltf) => {
          if (disposed) return;
          windrose = gltf.scene;
          windrose.traverse((child) => {
            const mesh = child as THREE.Mesh;
              if (mesh.isMesh) {
                
                if(child.name == "north" || child.name == "north_text") {
                  mesh.material = windrose_north_material;
                }else if(child.name == "compass_sphere"){
                   mesh.material = windrose_sphere_material;
                }else{
                  mesh.material = windrose_material;
                }
                mesh.renderOrder = 1;
              }
          });

          const box = new THREE.Box3().setFromObject(windrose);
          const center = box.getCenter(new THREE.Vector3());
          const radius = box.getBoundingSphere(new THREE.Sphere()).radius || 1;
          windrose.position.sub(center);
          windrose_holder.scale.setScalar(1 / radius);
          windrose_holder.add(windrose);
        },
        undefined,
        (error) => {
          if (disposed) return;
          console.error("BuildingModel: failed to load windrose GLB", error);
        },
    );

      loader.load(
        building_url,
        (gltf) => {
          if (disposed) return;
          model = gltf.scene;
          scene.add(model);

          model.traverse((obj) => {
            const mesh = obj as THREE.Mesh;
            if (mesh.isMesh && mesh.geometry && !mesh.geometry.attributes.normal) {
              mesh.geometry.computeVertexNormals();
            }
          });

          model.traverse((obj) => {
            if (!roof_name_pattern.test(obj.name)) return;
            obj.traverse((child) => {
              const mesh = child as THREE.Mesh;
              if (mesh.isMesh) mesh.material = roof_material;
            });
          });

          model.traverse((obj) => {
            if (!wall_name_pattern.test(obj.name)) return;
            obj.traverse((child) => {
              const mesh = child as THREE.Mesh;
              if (mesh.isMesh) mesh.material = wall_material;
            });
          });

          model.traverse((obj) => {
            if (obj.name !== "below_grade") return;
            obj.traverse((child) => {
              const mesh = child as THREE.Mesh;
              if (mesh.isMesh) mesh.material = below_grade_material;
            });
          });

          // A site plan tile replaces the grid ground when one is configured
          // and the model carries a georeference; otherwise the grid stays.
          const origin = gltf.scene.userData.origin_epsg25832;
          if (siteplan && origin) {
            const r = 75;
            const res = 1024;
            const bbox = [origin[0]-r, origin[1]-r, origin[0]+r, origin[1]+r].join(',');

            const texture_loader = new THREE.TextureLoader();
            const texture = texture_loader.load(
              siteplan.basemap.url_bbox_template.replace('{width}',res.toString()).replace('{height}',res.toString()).replace('{bbox}',bbox),
              () => {
                if (disposed || !floor_plan) {
                  texture.dispose();
                  return;
                }
                const material = floor_plan.material as THREE.MeshBasicMaterial;
                material.map = texture;
                material.needsUpdate = true;
              },
              undefined,
              (error) => {
                if (disposed) return;
                console.error("BuildingModel: failed to load site plan texture", error);
                texture.dispose();
              },
            );
            texture.colorSpace = THREE.SRGBColorSpace;
          }

          frame_object(model);
          setStatus("loaded");
        },
        undefined,
        (error) => {
          if (disposed) return;
          console.error("BuildingModel: failed to load GLB", building_url, error);
          setStatus("error");
        },
      );

      if(context_glb_url){
        loader.load(
          context_glb_url,
          (gltf) => {
            if (disposed) return;
            model = gltf.scene;
            scene.add(model);
            if(parcel_buildings){
              const parcel_buildings_names = parcel_buildings.map(pb => pb.building_id);
              model.traverse((obj) => {
                if(parcel_buildings_names.includes(obj.name)){
                  obj.traverse((child) => {
                    const mesh = child as THREE.Mesh;
                    if (mesh.isMesh) mesh.material = parcel_building_material;
                  });
                }else{
                   obj.traverse((child) => {
                    const mesh = child as THREE.Mesh;
                    if (mesh.isMesh) mesh.material = context_building_material;
                  });
                }
              });
            }
          },
          undefined,
          (error) => {
            if (disposed) return;
            console.error("BuildingModel: failed to load GLB", context_glb_url, error);
            setStatus("error");
          },
        );
      }
      
    }

    //onst geo = new THREE.PlaneGeometry(150, 150);
    const orbit_direction = new THREE.Vector3();

    const render = () => {
      controls.update();

      const w = container.clientWidth || 1;
      const h = container.clientHeight || 1;

      renderer.setScissorTest(false);
      renderer.setViewport(0, 0, w, h);
      renderer.clear();
      renderer.render(scene, camera);

      // Compass HUD: same viewing direction as the main camera, fixed size,
      // pinned to the top-left corner of the canvas.
      orbit_direction.copy(camera.position).sub(controls.target).normalize();
      overlay_camera.position.copy(orbit_direction).multiplyScalar(10);
      overlay_camera.up.copy(camera.up);
      overlay_camera.lookAt(0, 0, 0);

      const x = w - (overlay_size + overlay_margin_right);
      const y = h - (overlay_size + overlay_margin_top);
      renderer.setScissorTest(true);
      renderer.setScissor(x, y, overlay_size, overlay_size);
      renderer.setViewport(x, y, overlay_size, overlay_size);
      renderer.clearDepth();
      renderer.render(overlay_scene, overlay_camera);
      renderer.setScissorTest(false);

      frame_id = requestAnimationFrame(render);
    };
    render();

    const resize_observer = new ResizeObserver(() => {
      const w = container.clientWidth || 1;
      const h = container.clientHeight || 1;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    });
    resize_observer.observe(container);

    return () => {
      disposed = true;
      cancelAnimationFrame(frame_id);
      resize_observer.disconnect();
      controls.dispose();
      camera_ref.current = null;
      controls_ref.current = null;
      default_view_ref.current = null;
      roof_material.dispose();
      below_grade_material.dispose();
      windrose_material.dispose();
      windrose_north_material.dispose();
      windrose_sphere_material.dispose();
      parcel_building_material.dispose();
     context_building_material.dispose();
      stripe_texture.dispose();
      floor_plan_alpha_texture.dispose();
      const floor_plan_map = (floor_plan?.material as THREE.MeshBasicMaterial | undefined)?.map;
      if (floor_plan_map && floor_plan_map !== grid_texture) floor_plan_map.dispose();
      grid_texture.dispose();
      const dispose_object = (object: THREE.Object3D | undefined) => {
        if (!object) return;
        object.traverse((obj) => {
          const mesh = obj as THREE.Mesh;
          if (mesh.geometry) mesh.geometry.dispose();
          const material = mesh.material as
            | THREE.Material
            | THREE.Material[]
            | undefined;
          if (Array.isArray(material)) {
            material.forEach((m) => m.dispose());
          } else if (material) {
            material.dispose();
          }
        });
      };
      dispose_object(model);
      dispose_object(windrose);
      dispose_object(floor_plan);
      renderer.dispose();
      if (renderer.domElement.parentNode === container) {
        container.removeChild(renderer.domElement);
      }
    };
  }, [building_url]);

  return (
    <div className="building-model__container">
      {status == "loaded" && <div className="building-model__label">{<InfoButton modal={<ModalBuildingGeometryData/>}/>}<span>3D-Ansicht Ihres Gebäudes</span></div>}
      {status == "loaded" &&
        <div className="building-model__view-buttons">
          <button
            type="button"
            className="building-model__view-button"
            onClick={reset_view}
            aria-label="Kamera auf Startperspektive zurücksetzen"
          >
            Perspektive
          </button>
          <button
            type="button"
            className="building-model__view-button"
            onClick={top_view}
            aria-label="Kamera auf Vogelperspektive von oben setzen"
          >
            Lageplan
          </button>
        </div>
      }
      <div className="building-model" ref={container_ref}>
        {(loading_building_data || status === "loading") ? 
          <div className="building-model-status">Vorschau wird geladen…</div> : 
          (status == "loaded" ? <></> : <div className="building-model-status">Keine Vorschau verfügbar. (Nicht notwendig um Fortzufahren)</div>)
        }      
      </div>
    </div>
  );
}

/**
 * Builds a tiling texture of diagonal stripes for the below-grade material.
 */
function create_stripe_texture(): THREE.Texture {
  const size = 64;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d")!;

  ctx.fillStyle = "#c8c8c8";
  ctx.fillRect(0, 0, size, size);

  ctx.save();
  ctx.translate(size / 2, size / 2);
  ctx.rotate(Math.PI / 4);
  ctx.translate(-size, -size);
  ctx.fillStyle = "#555555";
  const stripe_width = size / 8;
  for (let y = 0; y < size * 2; y += stripe_width * 2) {
    ctx.fillRect(0, y, size * 2, stripe_width);
  }
  ctx.restore();

  const texture = new THREE.CanvasTexture(canvas);
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.repeat.set(4, 4);
  return texture;
}

// Fallback ground texture: a white/grey grid, used whenever no site plan tile
// is available (no model loaded yet, no site plan configured, tile request
// failed). `tiles` is how often the pattern repeats across the plane.
function create_grid_texture(tiles: number): THREE.Texture {
  const size = 128;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d")!;

  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, size, size);

  // Minor cells, five per tile.
  ctx.strokeStyle = "#c8c8c8";
  ctx.lineWidth = 2;
  ctx.beginPath();
  for (let i = 1; i < 5; i++) {
    const p = (size / 5) * i;
    ctx.moveTo(p, 0);
    ctx.lineTo(p, size);
    ctx.moveTo(0, p);
    ctx.lineTo(size, p);
  }
  ctx.stroke();

  // The tile border doubles as the major grid line.
  ctx.strokeStyle = "#9a9a9a";
  ctx.lineWidth = 4;
  ctx.strokeRect(0, 0, size, size);

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.generateMipmaps = false;
  // Nearest sampling keeps the grid lines crisp instead of smearing them.
  texture.magFilter = THREE.NearestFilter;
  texture.minFilter = THREE.NearestFilter;
  texture.repeat.set(tiles, tiles);
  return texture;
}
